"use client";

import { useState, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  TextField,
  Stack,
  Fade,
  Link as MuiLink,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  Link as LinkIcon,
  ContentCopy,
  CheckCircle,
  OpenInNew,
} from "@mui/icons-material";
import axios from "axios";
import { musicProviders } from "./providers";

interface MusicLinkConverterProps {
  sourceProvider: keyof typeof musicProviders;
  targetProvider: keyof typeof musicProviders;
}

export default function MusicLinkConverter({
  sourceProvider,
  targetProvider,
}: MusicLinkConverterProps) {
  const [url, setUrl] = useState<string>("");
  const [convertedLink, setConvertedLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [title, setTitle] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [artistName, setArtistName] = useState<string | null>(null);

  const sourceTitle = musicProviders[sourceProvider].title;
  const targetTitle = musicProviders[targetProvider].title;

  const handleConvert = useCallback(async () => {
    if (!url.trim()) {
      setError("Please enter a valid music link URL");
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setError("Please enter a valid URL");
      return;
    }

    setLoading(true);
    setError(null);
    setConvertedLink(null);
    setCopied(false);
    setTitle(null);
    setImage(null);
    setArtistName(null);

    try {
      const response = await axios.post("/api/song-link-finder", {
        link: url,
        provider: targetProvider,
      });

      if (response.data.link) {
        setConvertedLink(response.data.link);
        setTitle(response.data.title || null);
        setImage(response.data.image || null);
        setArtistName(response.data.artistName || null);
      } else {
        setError("Link not found for the target platform");
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          setError(
            `No ${targetTitle} link found for this song. The song may not be available on ${targetTitle}.`
          );
        } else if (err.response?.status === 400) {
          setError(
            err.response.data?.error ||
              "Invalid request. Please check your URL."
          );
        } else {
          setError(
            err.response?.data?.error ||
              "Failed to fetch link. Please try again later."
          );
        }
      } else if (err instanceof Error) {
        setError(err.message || "An unexpected error occurred");
      } else {
        setError("Failed to convert link. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [url, targetProvider, targetTitle]);

  const handleCopy = useCallback(async () => {
    if (convertedLink) {
      try {
        await navigator.clipboard.writeText(convertedLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = convertedLink;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand("copy");
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          setError("Failed to copy link");
        }
        document.body.removeChild(textArea);
      }
    }
  }, [convertedLink]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !loading) {
        handleConvert();
      }
    },
    [handleConvert, loading]
  );

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Input Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            component="p"
            fontWeight={600}
            sx={{ mb: 2 }}
            color="text.primary"
          >
            Enter {sourceTitle} Link
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              placeholder={`Paste your ${sourceTitle} link here...`}
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setError(null);
                setConvertedLink(null);
                setTitle(null);
                setImage(null);
                setArtistName(null);
              }}
              onKeyPress={handleKeyPress}
              disabled={loading}
              error={!!error && !convertedLink}
              InputProps={{
                startAdornment: (
                  <LinkIcon sx={{ color: "text.secondary", mr: 1 }} />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "1.1rem",
                },
              }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleConvert}
              disabled={loading || !url.trim()}
              sx={{
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: 600,
                background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #5568d9 30%, #653a92 90%)",
                },
                "&:disabled": {
                  background: "grey.300",
                },
              }}
            >
              {loading ? "Finding Link..." : `Convert to ${targetTitle}`}
            </Button>
          </Stack>
        </Box>

        {/* Loading Indicator */}
        {loading && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: "grey.200",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  background:
                    "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                },
              }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mt: 1 }}
            >
              Searching for {targetTitle} link...
            </Typography>
          </Box>
        )}

        {/* Error Message */}
        {error && !loading && (
          <Fade in={!!error}>
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </Fade>
        )}

        {/* Success Result */}
        {convertedLink && !loading && (
          <Fade in={!!convertedLink}>
            <Box>
              <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight={600} gutterBottom>
                  {targetTitle} Link Found!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your converted link is ready below.
                </Typography>
              </Alert>

              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  bgcolor: "grey.50",
                  border: "2px solid",
                  borderColor: "success.main",
                  borderRadius: 2,
                }}
              >
                <Stack spacing={2}>
                  {/* Song Info Section */}
                  {(title || image || artistName) && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 2,
                        bgcolor: "white",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      {image && (
                        <Avatar
                          src={image}
                          alt={title || "Album art"}
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 1,
                          }}
                          variant="rounded"
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {title && (
                          <Typography
                            variant="h6"
                            component="div"
                            sx={{
                              fontWeight: 600,
                              mb: 0.5,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {title}
                          </Typography>
                        )}
                        {artistName && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {artistName}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      {targetTitle} Link:
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: "white",
                      p: 1.5,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        flex: 1,
                        wordBreak: "break-all",
                        fontFamily: "monospace",
                        fontSize: "0.9rem",
                      }}
                    >
                      {convertedLink}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleCopy}
                      color={copied ? "success" : "default"}
                      title="Copy link"
                    >
                      {copied ? <CheckCircle /> : <ContentCopy />}
                    </IconButton>
                    <IconButton
                      size="small"
                      component={MuiLink}
                      href={convertedLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open link"
                    >
                      <OpenInNew />
                    </IconButton>
                  </Box>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopy />}
                      onClick={handleCopy}
                      fullWidth
                    >
                      {copied ? "Copied!" : "Copy Link"}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<OpenInNew />}
                      component={MuiLink}
                      href={convertedLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      fullWidth
                      sx={{
                        background:
                          "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                      }}
                    >
                      Open in {targetTitle}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Fade>
        )}

        {/* Instructions */}
        {!convertedLink && !loading && (
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ lineHeight: 1.8 }}
            >
              Simply paste a {sourceTitle} link above and we&#39;ll find the
              corresponding {targetTitle} link for you. Works with songs,
              albums, and playlists.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
